import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { type NavigateOptions, type To } from 'react-router-dom';
import { FiCornerRightDown } from 'react-icons/fi';
import { LuPackageOpen } from 'react-icons/lu'; // Icon for split into following
import type { Transaction, TagMap } from '../types';
import { formatCurrency } from '../utils/formatters';
import { useDisplayPrivacy } from '../contexts/DisplayPrivacyContext';
import { useAppDispatch, useAppSelector } from '../store/hooks'; // Import Redux hooks
import { splitTransaction, resetMutationStatus, mergeTransaction, fetchTransactionsForMonth, updateTransactionTag } from '../store/slices/transactionsSlice'; // Import Redux action
import { fetchTagsByType, selectTagsForTransactionType } from '../store/slices/tagsSlice';
import AmountInputModal from './AmountInputModal';
import ConfirmationDialog from './ConfirmationDialog';
import { formatTransactionHeaderDate, getCurrentKolkataYearMonth } from '../utils/dateTimeUtils';
import DraggableBottomSheet from './DraggableBottomSheet';
import TagSelector from './TagSelector';
import AccountIcon from './AccountIcon';
import CurrencyDisplay from './AmountDisplay';
import TransactionCard from './TransactionCard';
import { useNavigation } from '../hooks/useNavigation';

interface SplitTransactionViewProps {
    transaction: Transaction; // The initial transaction passed (could be parent or child)
    tagMap: TagMap;
    onClose: () => void;
    getTransactionDetailPath?: (transaction: Transaction) => To;
    getTransactionNavigationState?: (transaction: Transaction) => NavigateOptions['state'];
}

// Helper function to format date as "Day, Mon. D 'YY" e.g., "Wed, Apr. 2 '25"
const formatHeaderDate = (dateString: string): string => {
    return formatTransactionHeaderDate(dateString);
};

const SplitTransactionView: React.FC<SplitTransactionViewProps> = ({
    transaction: initialTransaction,
    tagMap,
    onClose,
    getTransactionDetailPath,
    getTransactionNavigationState,
}) => {
    const dispatch = useAppDispatch();
    const { navigateTo } = useNavigation();
    const { randomizeAmounts } = useDisplayPrivacy();
    const { transactions, mutationStatus, mutationError, tagUpdateStatus } = useAppSelector((state) => state.transactions);

    // State for the input modal
    const [isSplitInputOpen, setIsSplitInputOpen] = useState(false);
    const [transactionToMerge, setTransactionToMerge] = useState<Transaction | null>(null);
    const [selectedTransactionForTagId, setSelectedTransactionForTagId] = useState<number | null>(null);
    const [isTagSelectorOpen, setIsTagSelectorOpen] = useState(false);

    // Determine the transaction to display (always the parent)
    const displayTransaction = useMemo(() => {
        const targetId = initialTransaction.parentId ?? initialTransaction.id;
        // Find the most up-to-date version from the store
        return transactions.find(tx => tx.id === targetId) || null;
    }, [initialTransaction, transactions]);

    // --- Effect to handle mutation status changes --- 
    useEffect(() => {
        if (mutationStatus === 'succeeded' && isSplitInputOpen) {
            // If split succeeded while the modal was open, close the modal
            setIsSplitInputOpen(false);
            // Optionally: dispatch resetMutationStatus here or rely on onClose
        }
        // We don't automatically close the main view on success, only the input modal
    }, [mutationStatus, isSplitInputOpen]);

    // --- Effect to reset mutation status on close --- 
    useEffect(() => {
        return () => {
            // Reset status when the component unmounts (view is closed)
            dispatch(resetMutationStatus());
        };
    }, [dispatch]);

    // Function to open the split amount input modal
    const handleOpenSplitInput = () => {
        if (displayTransaction && !displayTransaction.parentId) {
            dispatch(resetMutationStatus()); // Clear previous errors before opening
            setIsSplitInputOpen(true);
        } else {
            // This case should ideally not happen if button is disabled correctly
            console.warn("Attempted to open split input for a child transaction.");
        }
    };

    // Function to handle the submission from the AddTransaction modal (in split mode)
    const handleSplitAmountSubmit = async (updatedTransaction: Transaction) => {
        if (!displayTransaction || !displayTransaction.id || displayTransaction.parentId) {
            console.error("Invalid state for splitting.");
            // Error should be shown via mutationError from slice if dispatch fails
            return;
        }

        const splitAmount1 = updatedTransaction.amount;
        const originalAmount = displayTransaction.amount;
        const splitAmount2 = parseFloat((originalAmount - splitAmount1).toFixed(2));

        // Basic validation (already done in modal, but good to double-check)
        if (splitAmount1 <= 0 || splitAmount2 <= 0) {
            console.error("Split amounts must be positive and less than the original.");
            // Rely on modal validation, but log error just in case
            // The slice will update mutationError if the dispatch fails server-side validation
            return;
        }

        try {
            console.log(`Dispatching split action for Tx ID ${displayTransaction.id} with amounts: ${splitAmount1}, ${splitAmount2}`);
            await dispatch(splitTransaction({
                transactionId: displayTransaction.id,
                amount1: splitAmount1,
                amount2: splitAmount2,
                tag: updatedTransaction.tag ?? null,
            })).unwrap(); // unwrap to catch rejection here

            console.log('Transaction split successfully via Redux.');
            // No need to manually update displayTransaction, it comes from useMemo -> Redux state
            // No need to call refetchData, slice update handles it
            // Modal closure is handled by useEffect watching mutationStatus

        } catch (err: any) { // Catch rejection from unwrap()
            console.error("Failed to dispatch split transaction via Redux:", err);
            // Error state (mutationError) is automatically set by the slice's rejected case
            // Keep the modal open so the user sees the error (mutationError will be displayed below)
        }
        // No finally block needed as loading state comes from mutationStatus
    };

    // Calculate total original amount (parent + children) using the derived displayTransaction
    const totalOriginalAmount = useMemo(() => {
        if (!displayTransaction) return 0;
        // If it's a parent, its amount is the remaining part. Sum with children from store.
        const subTotal = displayTransaction.subTransactions?.reduce((sum, tx) => sum + tx.amount, 0) || 0;
        return displayTransaction.amount + subTotal;
    }, [displayTransaction]);

    // Combine parent and sub-transactions for the list
    const combinedTransactions = useMemo(() => {
        if (!displayTransaction) return [];
        // Ensure parent is always last for rendering order
        const children = displayTransaction.subTransactions || [];
        const parent = { ...displayTransaction, subTransactions: undefined }; // Remove nested subs from parent copy
        return [...children, parent];
    }, [displayTransaction]);

    const selectedTransactionForTag = useMemo(() => {
        if (selectedTransactionForTagId == null) {
            return null;
        }

        return combinedTransactions.find((tx) => tx.id === selectedTransactionForTagId) ?? null;
    }, [combinedTransactions, selectedTransactionForTagId]);

    const tagSelectorAvailableTags = useAppSelector((state) =>
        selectTagsForTransactionType(state, selectedTransactionForTag?.type ?? 'DEBIT')
    );

    useEffect(() => {
        if (!selectedTransactionForTag) {
            return;
        }

        dispatch(fetchTagsByType(selectedTransactionForTag.type));
    }, [dispatch, selectedTransactionForTag]);

    const closeTagSelector = useCallback(() => {
        setIsTagSelectorOpen(false);
        setSelectedTransactionForTagId(null);
    }, []);

    const openTagSelector = useCallback((transaction: Transaction, event: React.MouseEvent) => {
        event.stopPropagation();
        setSelectedTransactionForTagId(transaction.id);
        setIsTagSelectorOpen(true);
    }, []);

    const openTransactionDetails = useCallback((transaction: Transaction) => {
        navigateTo(getTransactionDetailPath?.(transaction) ?? `/transactions/${transaction.id}`, {
            state: getTransactionNavigationState?.(transaction) ?? { transaction },
        });
    }, [getTransactionDetailPath, getTransactionNavigationState, navigateTo]);

    const handleUpdateTag = useCallback((newTag: string | null) => {
        if (tagUpdateStatus === 'loading') {
            return;
        }

        if (!selectedTransactionForTag) {
            return;
        }

        dispatch(updateTransactionTag({
            transactionId: selectedTransactionForTag.id,
            newTag,
            originalTransaction: selectedTransactionForTag,
        })).catch((error: unknown) => {
            console.error('Failed to update split transaction tag via Redux:', error);
        });
    }, [dispatch, selectedTransactionForTag, tagUpdateStatus]);

    // --- Loading and Error States --- 
    // Initial loading is removed - assuming transaction is already in the store
    // We rely on displayTransaction being found or not

    if (!displayTransaction) {
        // This might happen briefly if the store hasn't updated yet, or if the ID is invalid
        return (
            <div className="p-4 text-center text-muted-foreground">
                Loading transaction details or transaction not found...
            </div>
        );
    }
    // --- End Loading and Error States ---

    const isSplitting = mutationStatus === 'loading';
    const isMerging = mutationStatus === 'loading'; // Reuse for now
    const canSplit = !displayTransaction.parentId; // Only parent transactions can be split further
    const accountLastFour = displayTransaction.account?.accountNumber?.slice(-4);

    // Custom close handler for the modal to reset Redux state
    const handleCloseSplitInput = () => {
        setIsSplitInputOpen(false);
        dispatch(resetMutationStatus()); // Clear API errors when modal is closed manually
    };

    // --- Merge Handler ---
    const handleMergeClick = (childTransaction: Transaction) => {
        if (!childTransaction.id || !childTransaction.parentId) {
            console.error("Invalid transaction provided for merge.");
            return;
        }

        setTransactionToMerge(childTransaction);
    };

    const handleConfirmMerge = async () => {
        if (!transactionToMerge?.id) {
            return;
        }

        dispatch(resetMutationStatus()); // Clear previous errors
        try {
            console.log(`Dispatching merge action for Child Tx ID ${transactionToMerge.id}`);
            await dispatch(mergeTransaction(transactionToMerge.id)).unwrap();
            const { year: currentYear, month: currentMonth } = getCurrentKolkataYearMonth();
            dispatch(fetchTransactionsForMonth({ year: currentYear, month: currentMonth }));
            setTransactionToMerge(null);
            console.log("Transaction merged successfully via Redux.");
            // State updates automatically via Redux store changes -> re-render
            // Potentially close the view if the parent becomes the only transaction?
            // Or maybe update the view title/state if needed.

        } catch (err: any) {
            console.error("Failed to dispatch merge transaction via Redux:", err);
            // Error state (mutationError) is automatically set by the slice's rejected case
            // Error will be displayed below the list
        }
    };
    // --- End Merge Handler ---

    return (
        <>
            <div className="thin-scrollbar h-full overflow-y-auto pb-4 pt-0 text-foreground">

                {/* Original Transaction Summary Card */}
                <div className="mx-4 mt-1 flex-shrink-0 overflow-hidden rounded-3xl border border-border bg-card shadow-sm">
                    <div className="flex min-h-[96px] items-center justify-center px-6 py-5">
                        <CurrencyDisplay
                            amount={totalOriginalAmount}
                            type="DEBIT"
                            className="justify-center text-foreground"
                            valueClassName="text-[2.55rem] leading-none font-bold tracking-normal text-foreground"
                            symbolClassName="text-muted-foreground"
                            smallRupeeSymbol={true}
                            showFraction={true}
                        />
                    </div>
                    <div className="grid grid-cols-2 border-t border-border">
                        <div className="min-h-[64px] border-r border-border px-5 py-3">
                            <span className="block text-[0.64rem] font-bold uppercase tracking-wide text-muted-foreground">From</span>
                            <div className="mt-1.5 flex items-center gap-2">
                                {displayTransaction.account && (
                                    <div className="flex h-6 w-9 items-center justify-center rounded-md border border-border bg-background">
                                        <AccountIcon account={displayTransaction.account} className="h-4 w-6" showFallback />
                                    </div>
                                )}
                                <span className="truncate text-sm font-bold text-foreground">
                                    {accountLastFour ? `**${accountLastFour}` : displayTransaction.account?.name ?? 'Account'}
                                </span>
                            </div>
                        </div>
                        <div className="min-h-[64px] px-5 py-3">
                            <span className="block text-[0.64rem] font-bold uppercase tracking-wide text-muted-foreground">On</span>
                            <span className="mt-1.5 block text-sm font-bold text-foreground">
                                {formatHeaderDate(displayTransaction.transactionDate)}
                            </span>
                        </div>
                    </div>
                </div>

                {/* "Split Into" Section Header */}
                <div className="relative mx-4 flex h-16 items-center justify-center">
                    <div className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-border" />
                    <div className="relative inline-flex items-center gap-2 rounded-full bg-muted px-3.5 py-1.5 text-[0.68rem] font-bold uppercase tracking-wide text-foreground shadow-sm">
                        <LuPackageOpen className="h-3.5 w-3.5" />
                        {combinedTransactions.length === 1 ? 'Transaction is not split' : 'Split into the following'}
                    </div>
                </div>

                {/* List and Button Wrapper */}
                <div className="mx-4 mb-4 overflow-hidden rounded-3xl bg-muted p-2 dark:bg-card">
                    {/* List of transactions (Parent + Children) */}
                    <div className="space-y-2">
                        {combinedTransactions.length > 0 ? (
                            combinedTransactions.map((tx, index) => {
                                const isParent = tx.id === displayTransaction.id;
                                const showDivider = isParent && combinedTransactions.length > 1 && index > 0;
                                return (
                                    <React.Fragment key={tx.id}>
                                        {showDivider && (
                                            <div className="border-t-2 border-dashed border-background/80"></div>
                                        )}

                                        <div>
                                            <TransactionCard
                                                transaction={tx}
                                                tagMap={tagMap}
                                                onTagClick={openTagSelector}
                                                onCardClick={openTransactionDetails}
                                                className="rounded-3xl border border-border shadow-sm"
                                            />
                                        </div>
                                        {!isParent && (
                                            <div className="-mt-1 flex justify-end px-3">
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleMergeClick(tx);
                                                    }}
                                                    title="Merge back to parent"
                                                    className="flex items-center gap-1 rounded-full bg-background/80 px-3 py-1 text-xs font-semibold text-muted-foreground hover:bg-background hover:text-foreground"
                                                >
                                                    <span>Merge</span>
                                                    <FiCornerRightDown size={13} />
                                                </button>
                                            </div>
                                        )}
                                    </React.Fragment>
                                );
                            })
                        ) : (
                            <p className="text-center text-muted-foreground text-sm py-4">
                                No split details to display.
                            </p>
                        )}
                        {/* Display Redux mutation errors here */}
                        {mutationStatus === 'failed' && mutationError && (
                            <p className="text-center text-red-500 text-sm py-2 px-4">
                                {/* Display merge errors as well */}
                                Error: {mutationError}
                            </p>
                        )}
                        {/* Split Button Area */}
                        {canSplit && (
                            <button
                                onClick={handleOpenSplitInput}
                                disabled={isSplitting} // Disable based on Redux mutation status
                                className="w-full rounded-[20px] border border-border bg-secondary px-4 py-4 text-base font-bold text-primary shadow-sm hover:bg-secondary/80 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {combinedTransactions.length === 1 ? 'Split Transaction' : isSplitting ? 'Processing Split...' : "Split Further"}
                            </button>
                        )}
                        {!canSplit && (
                            <p className="text-center text-muted-foreground text-sm py-2">
                                This is part of a split and cannot be split further.
                            </p>
                        )}
                    </div>
                </div>

                {/* Conditionally render the generic AmountInputModal for split input */}
                {isSplitInputOpen && displayTransaction && (
                    <AmountInputModal
                        transaction={displayTransaction}
                        onSubmitTransaction={handleSplitAmountSubmit}
                        onClose={handleCloseSplitInput} // Use custom handler to reset Redux state
                        tagMap={tagMap}
                        mode="split" // Set mode to split
                        maxAmount={displayTransaction.amount} // Pass the remaining parent amount as max
                    />
                )}

                <ConfirmationDialog
                    isOpen={!!transactionToMerge}
                    title="Merge Transaction"
                    message={`Are you sure you want to merge this transaction (Amount: ${formatCurrency(transactionToMerge?.amount ?? 0, { randomize: randomizeAmounts })}) back into its parent?`}
                    confirmLabel="Merge"
                    isProcessing={isMerging}
                    onCancel={() => setTransactionToMerge(null)}
                    onConfirm={() => {
                        void handleConfirmMerge();
                    }}
                />

                <DraggableBottomSheet
                    isOpen={isTagSelectorOpen}
                    onClose={closeTagSelector}
                    title="Tag Transaction"
                    zIndex={70}
                    closeLoading={tagUpdateStatus === 'loading'}
                >
                    <TagSelector
                        onSelectTag={handleUpdateTag}
                        availableTags={tagSelectorAvailableTags}
                        tagMap={tagMap}
                        currentTagName={selectedTransactionForTag?.tag}
                        transaction={selectedTransactionForTag ?? undefined}
                        disabled={tagUpdateStatus === 'loading'}
                    />
                </DraggableBottomSheet>
            </div>
        </>
    );
};

export default SplitTransactionView; 
